import React, { useEffect } from "react";
import { connect } from "react-redux";
import CategoriesHeaders from "../../CategoriesHeaders";
import CategoriesLoading from "../../CategoriesHeaders/CategoriesLoading";
import Menu from "../../Menu";
import LoadingMenu from "../../Menu/LoadingMenu";
import GridView from "../../GridView";
import LoadingGrid from "../../GridView/LoadingGrid";
import ScrollView from "../../Menu/ScrollView";
import LoadingScroll from "../../ScrollViewLoading/Menu";
import ListViewOld from "../../ListViewOld";
import LoadingListOld from "../../ListViewOld/LoadingView";
import BubblesView from "../../BubblesView";
import BubbleLoading from "../../BubblesView/BubbleLoading";
import FilterResult from "../../ProductDetails/ProductFilter/FilterResult";
import { isStorefrontFoundationEnabled } from "../featureFlags";
import {
  CATEGORY_NAVIGATION_MODES,
  getCollapsibleCategoryIndexes,
  resolveCategoryNavigation,
} from "./navigation";
import CategoryExpansionControls from "./CategoryExpansionControls";
import "./UnifiedCategoryNavigation.css";

const renderLoadingContent = ({ layout, language, settings }) => {
  if (["list", "jumbo"].includes(layout)) {
    return <LoadingMenu settings={settings} />;
  }
  if (["grid", "grid2"].includes(layout)) {
    return <LoadingGrid cellHeight={240} settings={settings} />;
  }
  if (layout === "list-collapse") return <LoadingListOld language={language} />;
  if (layout === "bubbles") return <BubbleLoading settings={settings} />;
  return <LoadingScroll language={language} />;
};

const renderCategoryContent = ({
  filterApplied,
  history,
  layout,
  settings,
}) => {
  if (filterApplied && settings.enable_filter_and_sort) return <FilterResult />;
  if (["list", "jumbo"].includes(layout)) return <Menu />;
  if (["grid", "grid2"].includes(layout)) return <GridView history={history} />;
  if (layout === "list-collapse") return <ListViewOld />;
  if (layout === "bubbles") return <BubblesView history={history} />;
  return <ScrollView history={history} />;
};

export const UnifiedCategoryNavigation = ({
  filterApplied = false,
  history,
  language = "english",
  onSetAllCategories,
  openedCategories = [],
  openedCategoriesInteracted = false,
  rendered = false,
  secondLoading = false,
  selectedCategory = "all",
  settings = {},
  store = {},
}) => {
  const enabled = isStorefrontFoundationEnabled(settings);
  const navigation = resolveCategoryNavigation(settings);
  const categories = Array.isArray(store.categories) ? store.categories : [];
  const collapsibleIndexes = getCollapsibleCategoryIndexes(categories);
  const allExpanded =
    collapsibleIndexes.length > 0 &&
    collapsibleIndexes.every((index) => openedCategories.includes(index));
  const noneExpanded = collapsibleIndexes.every(
    (index) => !openedCategories.includes(index),
  );

  useEffect(() => {
    if (
      enabled &&
      navigation.mode === CATEGORY_NAVIGATION_MODES.COLLAPSIBLE &&
      !navigation.collapsedByDefault &&
      !openedCategoriesInteracted &&
      collapsibleIndexes.length
    ) {
      onSetAllCategories(true, collapsibleIndexes, openedCategories);
    }
  }, [
    collapsibleIndexes,
    enabled,
    navigation.collapsedByDefault,
    navigation.mode,
    onSetAllCategories,
    openedCategories,
    openedCategoriesInteracted,
  ]);

  if (!enabled) return null;

  const showHeader =
    navigation.showHeader &&
    !filterApplied &&
    (secondLoading || (rendered && categories.length));
  const showExpansionControls =
    navigation.mode === CATEGORY_NAVIGATION_MODES.COLLAPSIBLE &&
    !filterApplied &&
    !secondLoading &&
    rendered &&
    collapsibleIndexes.length > 0;
  return (
    <>
      {showHeader && (
        <div
          className={`sf-category-navigation${navigation.sticky ? " sf-category-navigation--sticky" : ""}`}
          data-navigation-mode={navigation.mode}
          data-navigation-variant={navigation.variant}
        >
          {secondLoading ? (
            <CategoriesLoading categoriesStyle={navigation.headerStyle} />
          ) : (
            <CategoriesHeaders
              categoriesStyle={navigation.headerStyle}
              history={history}
              selectedCategory={selectedCategory}
              store={store}
            />
          )}
        </div>
      )}
      {showExpansionControls && (
        <CategoryExpansionControls
          allExpanded={allExpanded}
          language={language}
          noneExpanded={noneExpanded}
          onSetAll={(expanded) =>
            onSetAllCategories(
              expanded,
              collapsibleIndexes,
              openedCategories,
            )
          }
        />
      )}
      <div data-category-content={navigation.variant}>
        {secondLoading
          ? renderLoadingContent({
              language,
              layout: navigation.layout,
              settings,
            })
          : rendered && categories.length
          ? renderCategoryContent({
              filterApplied,
              history,
              layout: navigation.layout,
              settings,
            })
          : null}
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  openedCategories: state.store.openedCategories || [],
  openedCategoriesInteracted: state.store.openedCategoriesInteracted,
});

const mapDispatchToProps = (dispatch) => ({
  onSetAllCategories: (expanded, indexes, openedCategories) => {
    indexes.forEach((index) => {
      if (openedCategories.includes(index) !== expanded) {
        dispatch({
          type: "UPDATE_CATEGORY_STATE",
          payload: { expanded, index, interacted: true },
        });
      }
    });
    dispatch({
      type: "SAVE_LAST_OPENED_CATEGORY",
      payload: expanded ? indexes[0] : null,
    });
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UnifiedCategoryNavigation);
